/**
 * Pickup Mode — full-screen calm view for parents.
 * Large child photo, current status, "I'm here for pickup" / "Running late",
 * real-time staff notification.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useChild } from "../lib/childContext";
import { api } from "../lib/api";
import Brand from "../components/Brand";
import { ArrowLeft, Clock, CheckCircle2, Send, Users } from "lucide-react";

const STATE_LABEL = {
  arrived: "settled in",
  playing: "playing",
  resting: "resting",
  eating: "having a quiet snack",
  learning: "curiously exploring",
  safe: "safely held",
};

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "moments ago";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return new Date(iso).toLocaleString();
}

export default function Pickup() {
  const { user } = useAuth();
  const { selected, list } = useChild();
  const navigate = useNavigate();
  const [arrival, setArrival] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickups, setPickups] = useState([]);
  // Iteration 23 — Tabulex parity: parent declares WHICH authorized adult
  // is doing the pickup. Empty = self.
  const [selectedAuthId, setSelectedAuthId] = useState("");

  // Load active arrival for the selected child (if any).
  useEffect(() => {
    if (!selected?.id) return;
    let alive = true;
    api
      .get("/pickup/active")
      .then((r) => {
        if (!alive) return;
        const mine = (r.data || []).find(
          (p) => p.child_id === selected.id && p.parent_id === user?.id
        );
        setArrival(mine || null);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [selected?.id, user?.id]);

  // Load trusted pickup authorizations for the child.
  useEffect(() => {
    if (!selected?.id) {
      setPickups([]);
      return;
    }
    let alive = true;
    api
      .get(`/children/${selected.id}/pickup-authorizations`)
      .then((r) => {
        if (alive) setPickups(r.data || []);
      })
      .catch(() => {
        if (alive) setPickups([]);
      });
    return () => {
      alive = false;
    };
  }, [selected?.id]);

  if (!user || user.role !== "parent") {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-6">
        <p className="font-serif italic text-[#6A6863]">Pickup mode is for parents.</p>
      </div>
    );
  }

  if (!selected) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex flex-col items-center justify-center px-6 gap-4">
        <p className="font-serif italic text-[#6A6863]">Choose a child first.</p>
        <button
          onClick={() => navigate("/home")}
          data-testid="pickup-back-to-home"
          className="btn-ghost"
        >
          Back home
        </button>
      </div>
    );
  }

  const announce = async (status, etaMinutes = null) => {
    setBusy(true);
    try {
      const r = await api.post("/pickup/arrive", {
        child_id: selected.id,
        status,
        eta_minutes: etaMinutes,
        pickup_auth_id: selectedAuthId || null,
      });
      setArrival(r.data);
      const picker =
        pickups.find((p) => p.id === selectedAuthId)?.name || "you";
      toast(
        status === "arrived"
          ? selectedAuthId
            ? `Staff know ${picker} is here.`
            : "Staff have been gently notified."
          : `Staff know ${selectedAuthId ? picker : "you're"} ~${etaMinutes} min away.`
      );
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't reach the center.");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!arrival) return;
    setBusy(true);
    try {
      await api.post(`/pickup/cancel/${arrival.id}`);
      setArrival(null);
      toast("Quietly cancelled.");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't cancel.");
    } finally {
      setBusy(false);
    }
  };

  const stateLabel = STATE_LABEL[selected.current_state] || "held safely";
  const caregiverName = selected.state_updated_by_name;

  return (
    <div data-testid="pickup-screen" className="min-h-screen bg-[#FBF9F6]">
      <header className="px-5 sm:px-10 pt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate("/home")}
          data-testid="pickup-back"
          aria-label="Back"
          className="flex items-center gap-2 text-[#6A6863] hover:text-[#2C2B29] transition-colors"
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
          <span className="text-sm">Home</span>
        </button>
        <Brand size="sm" dataTestId="brand-pickup" />
      </header>

      <main className="px-6 sm:px-10 pt-8 pb-32 max-w-md mx-auto fade-up">
        <span className="eyebrow">Pickup</span>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A1B2E]">
          Heading to{" "}
          <span className="text-[#1F3A2A] italic font-light">{selected.name}?</span>
        </h1>

        {/* Big photo */}
        <div
          data-testid="pickup-photo"
          className="mt-8 aspect-square rounded-3xl overflow-hidden bg-[#F2EFEA] border hairline relative"
        >
          {selected.portrait_url ? (
            <img
              src={selected.portrait_url}
              alt={selected.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="font-serif text-5xl italic text-[#8A9984]">
                {selected.name?.[0] || "—"}
              </span>
            </div>
          )}
        </div>

        {/* Current status */}
        <div className="mt-7 rounded-3xl bg-white border hairline p-6 shadow-[0_2px_8px_rgba(16,24,40,0.04)]">
          <span className="eyebrow">Right now</span>
          <p className="mt-3 text-2xl font-semibold text-[#34C759] leading-snug">
            {selected.name} is {stateLabel}.
          </p>
          {selected.group_name && (
            <p className="mt-2 text-sm text-[#6B7280]">In {selected.group_name}.</p>
          )}
          {caregiverName && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E6EFE8]">
              <span className="text-xs font-medium text-[#1F3A2A]">With Teacher {caregiverName}</span>
            </div>
          )}
          <p className="mt-3 text-xs text-[#6B7280] inline-flex items-center gap-1.5">
            <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-[#34C759]" />
            Updated {timeAgo(selected.state_updated_at)}
          </p>
        </div>

        {/* Active arrival banner */}
        {arrival && arrival.status !== "cancelled" && (
          <div
            data-testid="pickup-active-banner"
            className="mt-6 rounded-3xl bg-[#132619] text-white p-6 shadow-[0_8px_24px_rgba(26,27,46,0.18)]"
          >
            <span className="eyebrow text-[#E6EFE8]">
              {arrival.status === "arrived" ? "You let them know" : "On the way"}
            </span>
            <p className="mt-3 text-lg font-medium">
              {arrival.status === "arrived"
                ? `Staff have been notified. ${(caregiverName || "Your child's teacher")} is bringing ${selected.name} out.`
                : `Staff know you're about ${arrival.eta_minutes ?? "a few"} min away.`}
            </p>
            <button
              type="button"
              onClick={cancel}
              disabled={busy}
              data-testid="pickup-cancel"
              className="mt-5 text-[#E6EFE8] underline underline-offset-4 text-sm"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Actions */}
        {(!arrival || arrival.status === "cancelled") && (
          <div className="mt-8 space-y-4" data-testid="pickup-actions">
            {/* Picker selector — only authorized people can be chosen */}
            {pickups.length > 0 && (
              <div
                data-testid="pickup-picker-section"
                className="rounded-3xl bg-white border hairline p-5"
              >
                <div className="flex items-center gap-2">
                  <Users size={14} strokeWidth={1.5} className="text-[#8A9984]" />
                  <span className="eyebrow">Who is picking up?</span>
                </div>
                <select
                  value={selectedAuthId}
                  onChange={(e) => setSelectedAuthId(e.target.value)}
                  data-testid="pickup-picker-select"
                  className="orbit-input mt-3 bg-transparent w-full"
                >
                  <option value="">It&rsquo;s me</option>
                  {pickups.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.relation ? ` · ${p.relation.replace("_", " ")}` : ""}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-[11px] text-[#6A6863] italic">
                  Only the people you&rsquo;ve authorized are listed here.
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={() => announce("arrived")}
              disabled={busy}
              data-testid="pickup-here-btn"
              className="w-full rounded-2xl bg-[#1F3A2A] text-white py-5 text-lg font-semibold tracking-tight transition-all hover:bg-[#132C17] hover:-translate-y-0.5 shadow-[0_8px_24px_rgba(107,91,231,0.28)] flex items-center justify-center gap-3"
            >
              <CheckCircle2 size={22} strokeWidth={2} />
              I&rsquo;m here for pickup
            </button>

            <div className="rounded-3xl bg-white border hairline p-5">
              <div className="flex items-center gap-2">
                <Clock size={14} strokeWidth={1.5} className="text-[#8A9984]" />
                <span className="eyebrow">Running a little late?</span>
              </div>
              <p className="mt-2 text-sm text-[#6A6863]">
                Let staff know — they&rsquo;ll keep {selected.name} comfortable.
              </p>
              <div className="mt-4 grid grid-cols-3 gap-2">
                {[5, 10, 15].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => announce("running_late", m)}
                    disabled={busy}
                    data-testid={`pickup-eta-${m}`}
                    className="rounded-full border hairline bg-[#F2EFEA] py-3 text-sm font-light text-[#2C2B29] hover:bg-[#E6BFA6]/40 transition-colors"
                  >
                    {m} min
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Authorized pickup people */}
        <section className="mt-10" data-testid="pickup-trusted-section">
          <div className="flex items-center gap-2">
            <Users size={14} strokeWidth={1.5} className="text-[#8A9984]" />
            <span className="eyebrow">Authorized today</span>
          </div>
          {pickups.length === 0 ? (
            <p
              data-testid="pickup-trusted-empty"
              className="mt-4 font-serif italic text-[#6A6863] text-sm"
            >
              No trusted pickup adults yet. Add some quietly from{" "}
              <button
                type="button"
                onClick={() => navigate("/trust")}
                className="underline underline-offset-2"
              >
                Trust
              </button>
              .
            </p>
          ) : (
            <ol
              data-testid="pickup-trusted-list"
              className="mt-4 space-y-px bg-[#2C2B29]/8 border hairline rounded-3xl overflow-hidden"
            >
              {pickups.map((p) => {
                const limited = !!p.valid_until;
                return (
                  <li
                    key={p.id}
                    data-testid={`pickup-trusted-row-${p.id}`}
                    className="bg-[#FBF9F6] p-4 flex items-center gap-3"
                  >
                    <span
                      className="h-7 w-7 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: limited ? "#D4A85F" : "#1A1B2E" }}
                    >
                      <Users
                        size={12}
                        strokeWidth={1.5}
                        className="text-white"
                      />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-[15px] text-[#2C2B29]">
                        {p.name}
                      </div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-[#6A6863] mt-0.5">
                        {(p.relation || "").replace("_", " ")}
                        {limited ? ` · only ${p.valid_until}` : " · always"}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </section>

        {/* Reassurance */}
        <p className="mt-10 text-center text-xs text-[#6A6863] font-light italic">
          Human-updated by your caregiver. Never a camera, never a sensor.
        </p>
      </main>
    </div>
  );
}
